import RichText from 'components/UI/Richtext';
import { ReactElement, useContext } from 'react';
import {
  IComponentVideoPlayerFields,
  IFragmentContentfulHostedVideo,
  IFragmentRendiaVideo,
  IFragmentVimeoVideo,
  IFragmentYouTubeVideo,
  IFragmentFreshpaintVideo,
} from 'types/contentful';
import { useRouter } from 'next/router';
import SiteDataContext from 'context/SiteDataContext';
import { FRESHPAINT_VIDEO_URL } from 'lib/consts';

enum VideoTypeId {
  Vimeo = 'fragmentVimeoVideo',
  Youtube = 'fragmentYouTubeVideo',
  Contentful = 'fragmentContentfulHostedVideo',
  Rendia = 'fragmentRendiaVideo',
  Freshpaint = 'fragmentFreshpaintVideo',
}

type VideoTypes =
  | IFragmentContentfulHostedVideo
  | IFragmentVimeoVideo
  | IFragmentYouTubeVideo
  | IFragmentRendiaVideo
  | IFragmentFreshpaintVideo;

const isVimeoVideoGuard = (videoObject: VideoTypes): videoObject is IFragmentVimeoVideo => {
  return (videoObject as IFragmentVimeoVideo).sys.contentType.sys.id === VideoTypeId.Vimeo;
};

const isYoutubeVideoGuard = (videoObject: VideoTypes): videoObject is IFragmentYouTubeVideo => {
  return (videoObject as IFragmentYouTubeVideo).sys.contentType.sys.id === VideoTypeId.Youtube;
};
const isFreshpaintVideoGuard = (
  videoObject: VideoTypes
): videoObject is IFragmentFreshpaintVideo => {
  return (
    (videoObject as IFragmentFreshpaintVideo).sys.contentType.sys.id === VideoTypeId.Freshpaint
  );
};

const isRendiaVideoGuard = (videoObject: VideoTypes): videoObject is IFragmentRendiaVideo => {
  return (videoObject as IFragmentRendiaVideo).sys.contentType.sys.id === VideoTypeId.Rendia;
};

const Vimeo = (video: IFragmentVimeoVideo): ReactElement => {
  const src = `https://player.vimeo.com/video/${video.fields.videoId}?title=0&byline=0&portrait=0`;

  return (
    <>
      <iframe
        title="video"
        src={src}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
      ></iframe>
      <script src="https://player.vimeo.com/api/player.js"></script>
    </>
  );
};

const YouTube = (video: IFragmentYouTubeVideo): ReactElement => {
  const src = `https://www.youtube.com/embed/${video.fields.videoId}?controls=0`;

  return (
    <iframe
      src={src}
      title="YouTube video player"
      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    ></iframe>
  );
};

const Rendia = (video: IFragmentRendiaVideo & { url: URL }): ReactElement => {
  const src = `//share.rendia.com/whitelabel/load/${btoa(video.url.toString())}/${
    video.fields.videoId
  }`;
  return (
    <iframe
      src={src}
      title="Rendia Embed Player"
      frameBorder="0"
      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
      aria-label="Rendia Embed Player"
      onLoad={(evt) => ((evt.target as HTMLElement).style.visibility = 'visible')}
      scrolling="no"
    ></iframe>
  );
};

const ContentfulVideo = (video: IFragmentContentfulHostedVideo): ReactElement => {
  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      className="mx-auto"
      controls
      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
      preload="auto"
      poster={video.fields.previewImage.fields.file.url}
    >
      <source
        src={video.fields.video.fields.file.url}
        type={video.fields.video.fields.file.contentType}
      ></source>
    </video>
  );
};

const FreshpaintVideo = (video: IFragmentFreshpaintVideo): ReactElement => {
  return (
    <iframe
      src={`${FRESHPAINT_VIDEO_URL}/${video.fields.videoId}`}
      frameBorder="0"
      aria-label="Freshpaint Embed Player"
      title="Freshpaint Video Player"
      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    ></iframe>
  );
};

function fullUrl(domain: string, path: string): URL {
  const protocol = domain.startsWith('localhost') ? 'http' : 'https';

  return new URL(`${protocol}://${domain}${path}`);
}

export default function VideoPlayer({
  heading,
  video,
  backgroundColor,
  description,
}: IComponentVideoPlayerFields): ReactElement {
  const router = useRouter();
  const { domain } = useContext(SiteDataContext);

  const Comp = (): ReactElement => {
    if (isVimeoVideoGuard(video)) {
      return <Vimeo {...video} />;
    } else if (isYoutubeVideoGuard(video)) {
      return <YouTube {...video} />;
    } else if (isRendiaVideoGuard(video)) {
      return <Rendia {...video} url={fullUrl(domain, router.asPath)} />;
    } else if (isFreshpaintVideoGuard(video)) {
      return <FreshpaintVideo {...video} />;
    } else {
      return <ContentfulVideo {...video} />;
    }
  };

  return (
    <div className="mb-6">
      <div className="bg-transparent text-center pt-6 lg:pt-12">
        {heading && <h2 className="font-heading text-2xl lg:text-4xl font-extrabold">{heading}</h2>}
        {description && (
          <div className="px-4 lg:px-32 mt-4 text-left">
            <RichText document={description} />
          </div>
        )}
      </div>
      <div className={`bg-${backgroundColor} pb-4 lg:pb-16 text-center mt-20`}>
        <div className="inline-block -mt-4 lg:-mt-16 px-4 w-full lg:w-3/4">
          <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
            <Comp />
          </div>
        </div>
      </div>
    </div>
  );
}
